import { useCustomToast } from '@/hooks/use-custom-toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  TUpdateLabelTicketValidation,
  UpdateLabelTicketValidationSchema,
  useUpdateLabelTicketMutation,
} from '../../../api/update-label-ticket.api';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_LABEL_TICKET_QUERY_KEY } from '../../../api/get-list-label-ticket.api';
import { useTicketingModalRootActions } from '../../../../_shared/components/ticketing-modal-root/ticketing-modal-root.store';
import { handleDomBubbleEvent } from '@/utils/dom';
import { Input } from '@/components/_ui/input';
import { LABEL_TICKET_COLOR } from '../../../../_shared/constants/ticketing-color.constant';
import { cn } from '@/utils/cn';
import {
  ModalCloseButton,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import { Spinner } from '@/components/_ui/spinner';

const TOAST_MESSAGES_UPDATE_LABEL_TICKET = {
  UPDATE_LABEL_TICKET_ERROR: 'Error update label ticket, please try again!',
  UPDATE_LABEL_TICKET_SUCCESS: 'Label ticket updated successfully',
};

export type TUpdateLabelTicketModalType = 'UpdateLabelTicketModal';
export interface IUpdateLabelTicketModalProps {
  labelTicketId: string;
  name?: string | null;
  color?: string | null;
}
const UpdateLabelTicketModal = memo((props: IUpdateLabelTicketModalProps) => {
  // HOOKS
  const modalActions = useTicketingModalRootActions();
  const showToast = useCustomToast();

  // ----- Form -----
  const form = useForm<TUpdateLabelTicketValidation>({
    resolver: zodResolver(UpdateLabelTicketValidationSchema),
    mode: 'onChange',
  });

  // ----- Mutation -----
  const mutationUpdateLabelTicket = useUpdateLabelTicketMutation({
    mutationConfig: {
      onError: (error) => {
        const errorMessage =
          axiosExtractErrorMessage(error) || TOAST_MESSAGES_UPDATE_LABEL_TICKET.UPDATE_LABEL_TICKET_ERROR;
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        Promise.all([queryClient.invalidateQueries({ queryKey: [GET_LIST_LABEL_TICKET_QUERY_KEY] })]);
        modalActions.closeModal('UpdateLabelTicketModal');
        form.reset();
        showToast({
          message: TOAST_MESSAGES_UPDATE_LABEL_TICKET.UPDATE_LABEL_TICKET_SUCCESS,
          type: 'success',
        });
      },
    },
  });

  // HELPERS
  const isOpenUpdateLabelTicketModal = useMemo(() => {
    return modalActions.checkIsOpenModal('UpdateLabelTicketModal');
  }, [modalActions]);

  const isDisabledForm = useMemo(() => {
    return mutationUpdateLabelTicket.isPending;
  }, [mutationUpdateLabelTicket.isPending]);

  const isLoadingForm = useMemo(() => {
    return mutationUpdateLabelTicket.isPending;
  }, [mutationUpdateLabelTicket.isPending]);

  const checkIsSelectedColor = (color: string) => {
    return form.watch('color') === color;
  };

  // EFFECTS
  useEffect(() => {
    if (props) {
      form.reset({
        labelTicketId: props?.labelTicketId || '',
        name: props?.name || '',
        color: props?.color || '',
      });
    }
  }, [props, form]);

  // HANDLERS
  const handleSubmitForm = (data: TUpdateLabelTicketValidation) => {
    mutationUpdateLabelTicket.mutate({
      labelTicketId: data?.labelTicketId || '',
      name: data?.name,
      color: data?.color,
    });
  };

  const handleSelectColor = (color: string) => {
    form.setValue('color', color);
  };

  const handleCloseModal = () => {
    modalActions.closeModal('UpdateLabelTicketModal');
  };

  const handleClose = useCallback(() => {
    handleCloseModal();
    form.reset();
  }, [handleCloseModal, form]);

  return (
    <Modal
      isOpen={isOpenUpdateLabelTicketModal}
      lockBodyScroll
      onClose={handleClose}
      className="p-[32px] md:w-[500px] w-[345px]"
    >
      <ModalHeader>
        <ModalTitle>Edit Label Ticketing</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(handleSubmitForm)(e);
          }}
          id="edit-label-ticket-form"
          className="grid gap-[16px]"
        >
          <div className="grid gap-[4px]">
            <label htmlFor="name-label-customer" className="text-[#4B4B4B] text-[14px] font-normal">
              Name*
            </label>
            <Input id="name-label-customer" placeholder="Enter Label Case Name" {...form.register('name')} />
            {form.formState.errors.name && (
              <p className="text-red-500 text-[12px] font-normal">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div className="grid gap-[4px]">
            <label htmlFor="color-label-customer" className="text-[#4B4B4B] text-[14px] font-normal">
              Color*
            </label>
            <div id="color-label-customer" className="grid grid-cols-3 gap-[22px]">
              {Object.entries(LABEL_TICKET_COLOR).map(([key, color], index) => {
                const isSelected = checkIsSelectedColor(color);
                return (
                  <button
                    type="button"
                    onClick={(e) => {
                      handleDomBubbleEvent(e);
                      handleSelectColor(color);
                    }}
                    key={index + 1}
                    className={cn(
                      'w-full gap-[6px] flex justify-start py-[8px] px-[6px] rounded-[8px] bg-[#F1F5F9] cursor-pointer border-transparent border-2 hover:border-[#0C8DBF] hover:bg-[#0C8DBF]/20 transition-all duration-300',
                      isSelected && 'border-[#0C8DBF] bg-[#0C8DBF]/20'
                    )}
                  >
                    <div className="w-[16px] h-[16px] rounded-full " style={{ backgroundColor: color }}></div>
                    <span className="text-[#25151E] text-[12px] font-normal">{key}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="edit-label-ticket-form" type="submit" disabled={isDisabledForm}>
          {isLoadingForm ? <Spinner className="w-[18px] h-[18px]" /> : null} Update
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
UpdateLabelTicketModal.displayName = 'UpdateLabelTicketModal';

export default UpdateLabelTicketModal;
